import React, { useState, useEffect } from 'react';
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TextField,
    InputAdornment,
    Typography,
    Chip,
    Button,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Main from '../layouts/Main';
import BlogPostDialog from '../components/BlogPostDialog';

const BlogPosts = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [categories, setCategories] = useState([]);

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
    const [selectedPost, setSelectedPost] = useState(null);

    // Delete confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [postToDelete, setPostToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const url = `https://api.elnaf.fi/api/get_blog_admin.php?page=${page}&q=${encodeURIComponent(searchTerm)}`;
            const response = await fetch(url);
            const data = await response.json();
            setPosts(data.posts || []);
            setTotalPages(data.totalPages || 1);
            setTotal(data.total || 0);
        } catch (error) {
            console.error('Error loading blog posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch('https://api.elnaf.fi/api/get_blog_categories_admin.php');
            const data = await response.json();
            setCategories(data || []);
        } catch (error) {
            console.error('Error loading blog categories:', error);
        }
    };

    useEffect(() => {
        fetchPosts();
    }, [page, searchTerm]);

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const handleAddClick = () => {
        setSelectedPost(null);
        setDialogMode('add');
        setDialogOpen(true);
    };

    const handleEditClick = (post) => {
        setSelectedPost(post);
        setDialogMode('edit');
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedPost(null);
    };

    const handleDialogSave = async () => {
        setDialogOpen(false);
        setSelectedPost(null);
        await fetchPosts();
    };

    const handleDeleteClick = (post) => {
        setPostToDelete(post);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!postToDelete) return;
        setDeleting(true);
        try {
            const response = await fetch('https://api.elnaf.fi/api/delete_blog_post.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: postToDelete.id })
            });
            const data = await response.json();
            if (data.status === 'success') {
                await fetchPosts();
            }
        } catch (error) {
            console.error('Delete error:', error);
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
            setPostToDelete(null);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    return (
        <Main>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Blog Posts</h1>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">Home</li>
                        <li className="breadcrumb-item active" aria-current="page">Blog Posts</li>
                    </ol>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card custom-card">
                        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="card-title">Blog Posts Management ({total} posts)</div>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddClick}
                                size="small"
                            >
                                New Post
                            </Button>
                        </div>
                        <div className="card-body">
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Search blog posts..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            {loading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell width={60}>Image</TableCell>
                                                    <TableCell>Title (EN)</TableCell>
                                                    <TableCell>Category</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell align="center">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {posts.map((post) => (
                                                    <TableRow key={post.id} hover>
                                                        <TableCell>
                                                            {post.image ? (
                                                                <img
                                                                    src={post.image}
                                                                    alt=""
                                                                    style={{ width: 50, height: 35, objectFit: 'cover', borderRadius: 4 }}
                                                                />
                                                            ) : (
                                                                <Box sx={{ width: 50, height: 35, bgcolor: '#f0f0f0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <Typography variant="caption" color="text.secondary">—</Typography>
                                                                </Box>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {post.title_en || '(No title)'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                                /{post.slug}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={post.category?.en || 'Uncategorized'}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={post.status === 1 ? 'Active' : 'Inactive'}
                                                                size="small"
                                                                color={post.status === 1 ? 'success' : 'default'}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="caption">
                                                                {formatDate(post.created_at)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <IconButton size="small" onClick={() => handleEditClick(post)} title="Edit">
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => handleDeleteClick(post)} title="Delete" color="error">
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {totalPages > 1 && (
                                        <Box display="flex" justifyContent="center" mt={3}>
                                            <Pagination
                                                count={totalPages}
                                                page={page}
                                                onChange={(e, val) => setPage(val)}
                                                color="primary"
                                            />
                                        </Box>
                                    )}
                                </>
                            )}

                            {!loading && posts.length === 0 && (
                                <Box textAlign="center" py={5}>
                                    <Typography variant="h6" color="text.secondary">
                                        {searchTerm ? 'No posts found matching your search.' : 'No blog posts yet.'}
                                    </Typography>
                                </Box>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add/Edit Dialog */}
            <BlogPostDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                post={selectedPost}
                mode={dialogMode}
                onSave={handleDialogSave}
                categories={categories}
            />

            {/* Delete Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
                <DialogTitle>Delete this blog post?</DialogTitle>
                <DialogContent>
                    <Typography>
                        <strong>{postToDelete?.title_en}</strong> will be permanently deleted.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Main>
    );
};

export default BlogPosts;
